import { mergeScanLatest, utils } from '@neo-one/utils';
import debug from 'debug';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { UnknownAccountError, UnknownNetworkError } from '../../errors';
const logger = debug('NEOONE:HDKeystore');
const flattenAccounts = (accounts) => _.flatten(Object.values(accounts)
    .filter(utils.notNull)
    .map((networkAccounts) => Object.values(networkAccounts))).filter(utils.notNull);
const getNewNetworks = (networks, networksIn) => {
    if (networks === undefined) {
        return networksIn;
    }
    return networksIn.filter((network) => !networks.has(network));
};
export class HDKeyStore {
    constructor(provider, handler) {
        this.provider = provider;
        this.handler = handler;
        this.logger = handler.suffix ? debug(`NEOONE:HDKeystore:${handler.suffix}`) : debug('NEOONE:HDKeystore');
        this.byteLimit = handler.byteLimit;
        this.accountsInternal$ = new BehaviorSubject({});
        this.accountsSubscription = this.provider.networks$
            .pipe(mergeScanLatest(async (acc, networks) => {
            const networksFiltered = getNewNetworks(acc === undefined ? undefined : acc.scannedNetworks, networks);
            const next = await Promise.all(networksFiltered.map(async (network) => this.handler.scanAccounts(network, 5)));
            const newAccounts = next.reduce((accum, newAccountsIn) => accum.concat(newAccountsIn), []);
            const nextAccounts = newAccounts.reduce((accum, account) => ({
                ...accum,
                [account.userAccount.id.network]: {
                    ...(accum[account.userAccount.id.network] === undefined ? {} : accum[account.userAccount.id.network]),
                    [account.userAccount.id.address]: account,
                },
            }), acc === undefined ? {} : acc.accounts);
            return {
                accounts: nextAccounts,
                scannedNetworks: acc === undefined
                    ? new Set()
                    : new Set([...acc.scannedNetworks].concat(networksFiltered)),
            };
        }), map(({ accounts }) => accounts))
            .subscribe(this.accountsInternal$);
        this.accounts$ = this.accountsInternal$.pipe(distinctUntilChanged((a, b) => _.isEqual(a, b)), map(flattenAccounts));
        this.userAccountsInternal$ = new BehaviorSubject([]);
        this.accounts$
            .pipe(map((accounts) => accounts.map(({ userAccount }) => userAccount)))
            .subscribe(this.userAccountsInternal$);
        this.userAccounts$ = this.userAccountsInternal$;
        this.currentUserAccountInternal$ = new BehaviorSubject(undefined);
        this.accounts$
            .pipe(map((accounts) => {
            if (accounts.length === 0) {
                return undefined;
            }
            return accounts[0].userAccount;
        }))
            .subscribe(this.currentUserAccountInternal$);
        this.currentUserAccount$ = this.currentUserAccountInternal$.pipe(distinctUntilChanged());
    }
    getCurrentUserAccount() {
        return this.currentUserAccountInternal$.getValue();
    }
    getUserAccounts() {
        return this.userAccountsInternal$.getValue();
    }
    getNetworks() {
        return this.provider.getNetworks();
    }
    async selectUserAccount(id) {
        if (id === undefined) {
            this.currentUserAccountInternal$.next(undefined);
        }
        else {
            const { userAccount } = this.getHDAccount(id);
            this.currentUserAccountInternal$.next(userAccount);
        }
    }
    async sign({ account, message, }) {
        return this.capture(async () => {
            const hdAccount = this.getHDAccount(account);
            const response = await this.handler.sign({
                message: Buffer.from(message, 'hex'),
                account: hdAccount.identifier,
            });
            return response.toString('hex');
        }, 'neo_sign');
    }
    async close() {
        this.accountsSubscription.unsubscribe();
        await this.handler.close();
    }
    get accounts() {
        return this.accountsInternal$.getValue();
    }
    getHDAccount({ address, network }) {
        const accounts = this.accounts[network];
        if (accounts === undefined) {
            throw new UnknownNetworkError(network);
        }
        const account = accounts[address];
        if (account === undefined) {
            throw new UnknownAccountError(address);
        }
        return account;
    }
    async capture(func, title) {
        try {
            const result = await func();
            logger('%o', { title, level: 'debug' });
            return result;
        }
        catch (error) {
            logger('%o', { title, level: 'error', error: error.message });
            throw error;
        }
    }
}

//# sourceMappingURL=HDKeyStore.js.map
